export function todayIso(): string {
  const d = new Date();
  return toIso(d);
}

export function toIso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// "YYYY-MM-DD" -> local-midnight Date. Bridges a stored ISO string to the Date the calendar consumes.
export function fromIso(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// Inclusive array of ISO date strings from start for `days` days.
export function dayRange(startIso: string, days: number): string[] {
  const [y, m, d] = startIso.split("-").map(Number);
  const out: string[] = [];
  for (let i = 0; i < days; i++) {
    const dt = new Date(y, m - 1, d + i);
    out.push(toIso(dt));
  }
  return out;
}

export function shiftIso(startIso: string, deltaDays: number): string {
  const [y, m, d] = startIso.split("-").map(Number);
  return toIso(new Date(y, m - 1, d + deltaDays));
}

// "YYYY-MM-DD" -> "D/M" (no leading zeros, no year).
export function toDayMonth(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${Number(d)}/${Number(m)}`;
}

// "YYYY-MM-DD" -> "D/M-YYYY" (no leading zeros).
export function toDayMonthYear(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${Number(d)}/${Number(m)}-${y}`;
}

// "HH:mm:ss" (or "HH:mm") -> "HH:mm". Backend serializes TimeOnly as "18:30:00".
export function toHourMinute(t: string): string {
  return t.slice(0, 5);
}

// Day of week for an ISO date: 0=Sunday .. 6=Saturday.
export function weekdayIndex(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).getDay();
}

// Full Swedish weekday names, indexed by `weekdayIndex` (0=Sun .. 6=Sat).
const WEEKDAY_LONG = [
  "Söndag",
  "Måndag",
  "Tisdag",
  "Onsdag",
  "Torsdag",
  "Fredag",
  "Lördag",
] as const;

// "YYYY-MM-DD" -> full Swedish weekday name.
export function toWeekdayName(iso: string): string {
  return WEEKDAY_LONG[weekdayIndex(iso)];
}

// Short Swedish weekday names, indexed by `weekdayIndex` (0=Sun .. 6=Sat).
const WEEKDAY_SHORT = [
  "Sön",
  "Mån",
  "Tis",
  "Ons",
  "Tor",
  "Fre",
  "Lör",
] as const;

// "YYYY-MM-DD" -> short Swedish weekday name ("Ons").
export function toWeekdayShort(iso: string): string {
  return WEEKDAY_SHORT[weekdayIndex(iso)];
}

// Short Swedish month names, indexed by (month number − 1).
// biome-ignore format: calendar row reads best on one line
const MONTH_SHORT = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"] as const;

// "YYYY-MM-DD" -> short Swedish month name ("okt").
export function toMonthShort(iso: string): string {
  return MONTH_SHORT[monthOf(iso) - 1];
}

// Month number (1-12) of a "YYYY-MM-DD" date.
export function monthOf(iso: string): number {
  return Number(iso.slice(5, 7));
}

// Zero-padded day-of-month label ("01".."31") — a display string (contrast monthOf, which returns a number).
export function dayOfMonthLabel(iso: string): string {
  return iso.slice(8);
}

export function isFirstOfMonth(iso: string): boolean {
  return dayOfMonthLabel(iso) === "01";
}

// Most recent date on or before `iso` on weekday `dow` (0=Sun .. 6=Sat); returns `iso` if it matches.
export function startOfWeekOn(iso: string, dow: number): string {
  const back = (weekdayIndex(iso) - dow + 7) % 7;
  return shiftIso(iso, -back);
}

// Is `dayIso` covered by [startIso, endIso] inclusive?
export function isWithin(
  dayIso: string,
  startIso: string,
  endIso: string,
): boolean {
  return dayIso >= startIso && dayIso <= endIso;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}
